
import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6bm5l3v53dtlm7j7ok0';

export default function EmergencyReportForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const description = formData.get('description') as string;
    if (description && description.length > 500) return;

    setIsSubmitting(true);
    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        urlEncoded.append(key, value.toString());
      });

      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });
      setIsSuccess(true);
      form.reset();
      setCharCount(0);
    } catch {
      setIsSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section className="py-20 bg-gray-50">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="bg-white rounded-2xl border border-gray-200 p-12 shadow-sm">
            <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-teal-600 text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Report Submitted</h3>
            <p className="text-gray-600 text-sm mb-6">Thank you for your report. If this is an active emergency, please call 911 immediately.</p>
            <button
              onClick={() => setIsSuccess(false)}
              className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              Submit Another Report
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 text-teal-600 bg-teal-50 text-sm font-medium px-4 py-1.5 rounded-full mb-4">
            <i className="ri-file-warning-line"></i>
            Non-Emergency Reports
          </span>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Report a Non-Emergency Issue</h2>
          <p className="text-gray-600 text-sm max-w-lg mx-auto">
            Use this form for non-urgent reports such as potholes, streetlight outages, graffiti, or neighborhood concerns. <strong>For emergencies, always call 911.</strong>
          </p>
        </div>

        <form
          id="emergency-report-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm space-y-5"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Your Name</label>
              <input
                type="text"
                name="name"
                required
                placeholder="First and last name"
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
              <input
                type="email"
                name="email"
                required
                placeholder="you@example.com"
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
            <input
              type="tel"
              name="phone"
              placeholder="(916) 555-0123"
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Issue Type</label>
            <select
              name="issue_type"
              required
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all bg-white cursor-pointer"
            >
              <option value="">Select an issue type</option>
              <option value="Road / Pothole">Road / Pothole</option>
              <option value="Streetlight Outage">Streetlight Outage</option>
              <option value="Graffiti / Vandalism">Graffiti / Vandalism</option>
              <option value="Noise Complaint">Noise Complaint</option>
              <option value="Abandoned Vehicle">Abandoned Vehicle</option>
              <option value="Water / Sewer Issue">Water / Sewer Issue</option>
              <option value="Tree / Vegetation Hazard">Tree / Vegetation Hazard</option>
              <option value="Illegal Dumping">Illegal Dumping</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Location of Issue</label>
            <input
              type="text"
              name="location"
              required
              placeholder="Street address or nearest intersection"
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
            <textarea
              name="description"
              rows={4}
              required
              maxLength={500}
              placeholder="Please describe the issue in detail..."
              onChange={(e) => setCharCount(e.target.value.length)}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all resize-none"
            ></textarea>
            <p className={`text-xs mt-1 text-right ${charCount > 450 ? 'text-orange-500' : 'text-gray-400'}`}>
              {charCount}/500 characters
            </p>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white py-3 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Submitting...
              </>
            ) : (
              <>
                <i className="ri-send-plane-line"></i>
                Submit Report
              </>
            )}
          </button>

          <p className="text-xs text-gray-400 text-center">
            This form is for non-emergency reports only. For emergencies, call <a href="tel:911" className="text-red-500 font-semibold hover:underline cursor-pointer">911</a>.
          </p>
        </form>
      </div>
    </section>
  );
}
